/**
 * Embed Key Authentication Provider Module
 * Authenticates an embedded standalone chat using a PUBLIC, domain-restricted
 * embed key (pk_embed_...). The embed key is exchanged server-side (POST
 * /api/public/embed-config/session) for a short-lived Bearer token, so the
 * secret API key never reaches the browser (GitHub #1024).
 *
 * Mirrors ApiKeyProvider's interface so it slots into ConditionalAuthProvider.
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { api } from '@/utils/api-client'
import { setAuthTokens, clearAuthTokens } from '@/utils/auth-storage'
import LoadingSpinner from './loading-spinner'

/**
 * Props for the EmbedKeyProvider component
 * @property {string} embedKey - The public embed key to authenticate with
 * @property {ReactNode} children - Children rendered after authentication
 */
interface EmbedKeyProviderProps {
  embedKey: string
  children: ReactNode
}

/** Authenticated user data (simplified, compatible with session auth) */
interface EmbedKeyUser {
  id: string
  name: string
  email: string
}

/** Workspace data associated with the embed session */
interface EmbedKeyWorkspace {
  id: string
  name: string
}

/** Context for embed-key authentication state */
interface EmbedKeyAuthContext {
  user: EmbedKeyUser | null
  workspace: EmbedKeyWorkspace | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

const defaultContextValue: EmbedKeyAuthContext = {
  user: null,
  workspace: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
}

const EmbedKeyAuthContext = createContext<EmbedKeyAuthContext>(defaultContextValue)

/**
 * Resolves the stable visitor device id for the embed session exchange.
 * Priority: the `deviceId` URL param injected by the bubble loader (stored
 * first-party in the HOST page's localStorage — survives third-party iframe
 * storage partitioning). Fallback for plain-iframe embeds without the loader:
 * this iframe's own localStorage, which browsers partition per embedding site
 * — still stable per site, just not shared across sites. Returns undefined
 * when storage is unavailable; the server then mints an ephemeral guest.
 */
function resolveDeviceId(): string | undefined {
  const DEVICE_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/
  const fromUrl = new URLSearchParams(window.location.search).get('deviceId')
  if (fromUrl && DEVICE_ID_PATTERN.test(fromUrl)) return fromUrl

  try {
    const KEY = 'taqat_device_id'
    const existing = window.localStorage.getItem(KEY)
    if (existing && DEVICE_ID_PATTERN.test(existing)) return existing
    const fresh = crypto.randomUUID().replace(/-/g, '')
    window.localStorage.setItem(KEY, fresh)
    return fresh
  } catch {
    return undefined
  }
}

/**
 * Hook to access embed-key authentication context
 * @returns The current embed-key authentication state
 */
export function useEmbedKeyAuth(): EmbedKeyAuthContext {
  const context = useContext(EmbedKeyAuthContext)
  if (!context) {
    throw new Error('useEmbedKeyAuth must be used within an EmbedKeyProvider')
  }
  return context
}

/**
 * Embed Key Authentication Provider Component
 * Exchanges the public embed key for a Bearer token, stores it, then validates
 * the session — identical downstream flow to the legacy api-key exchange.
 *
 * @param props - Component props
 * @returns Provider component with embed-key authentication
 */
export function EmbedKeyProvider({ embedKey, children }: EmbedKeyProviderProps) {
  const [user, setUser] = useState<EmbedKeyUser | null>(null)
  const [workspace, setWorkspace] = useState<EmbedKeyWorkspace | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isAuthenticated = user !== null && workspace !== null

  useEffect(() => {
    const exchangeEmbedKeyForToken = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Step 1: Exchange the public embed key for a Bearer token.
        // The deviceId lets the server mint a stable per-visitor GUEST
        // session (returning device → same guest + chat history) instead of
        // an ephemeral one.
        const deviceId = resolveDeviceId()
        const tokenResponse = await api.post('/public/embed-config/session', {
          embedKey,
          ...(deviceId ? { deviceId } : {}),
        }, {
          requiresAuth: false,
          showErrorToast: false,
        })

        if (!tokenResponse.success || !tokenResponse.data) {
          throw new Error(tokenResponse.error || 'Failed to exchange embed key for token')
        }

        const { accessToken, refreshToken, expiresAt } = tokenResponse.data

        // Step 2: Store tokens (same as normal login)
        setAuthTokens({
          accessToken,
          refreshToken,
          expiresAt: new Date(expiresAt).getTime(),
        })

        // Step 3: Validate session to hydrate user/workspace
        const sessionResponse = await api.get('/auth/session', {
          requiresAuth: true,
          showErrorToast: false,
        })

        if (sessionResponse.success && sessionResponse.data) {
          const { user: userData, workspace: workspaceData } = sessionResponse.data
          setUser({
            id: userData.id,
            name: userData.name || userData.email,
            email: userData.email,
          })
          setWorkspace({
            id: workspaceData.id,
            name: workspaceData.name,
          })
        } else {
          throw new Error('Failed to validate session after token exchange')
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed'
        setError(errorMessage)
        setUser(null)
        setWorkspace(null)
        clearAuthTokens()
      } finally {
        setIsLoading(false)
      }
    }

    if (embedKey) {
      exchangeEmbedKeyForToken()
    } else {
      setError('No embed key provided')
      setIsLoading(false)
    }
  }, [embedKey])

  const contextValue: EmbedKeyAuthContext = {
    user,
    workspace,
    isAuthenticated,
    isLoading,
    error,
  }

  if (isLoading) {
    return <LoadingSpinner message="Authenticating embed..." />
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center p-6 max-w-md">
          <h2 className="text-lg font-semibold mb-2 text-destructive">
            Authentication Failed
          </h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <p className="text-xs text-muted-foreground">
            Please check the embed configuration and try again.
          </p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center p-6 max-w-md">
          <h2 className="text-lg font-semibold mb-2 text-destructive">
            Authentication Error
          </h2>
          <p className="text-muted-foreground">
            Unable to authenticate with the provided embed key.
          </p>
        </div>
      </div>
    )
  }

  return (
    <EmbedKeyAuthContext.Provider value={contextValue}>
      {children}
    </EmbedKeyAuthContext.Provider>
  )
}

EmbedKeyProvider.displayName = 'EmbedKeyProvider'
